import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./ResturantDetails.css";

export default function RestaurantData() {
  const [restaurants, setRestaurants] = useState([]);
  const [filters, setFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/resturant/list`)
      .then((res) => {
        setRestaurants(res.data);
        initFilters(res.data);
      })
      .catch(() => toast.error("❌ Failed to load restaurants"));
  }, []);

  const initFilters = (data) => {
    const statuses = Array.from(
      new Set(data.map((r) => r.status).filter(Boolean))
    );

    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      restaurantId: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      address: { value: null, matchMode: FilterMatchMode.CONTAINS },
      city: { value: null, matchMode: FilterMatchMode.CONTAINS },
      contactPerson: { value: null, matchMode: FilterMatchMode.CONTAINS },
      phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    setStatusOptions(statuses);
  };

  const [statusOptions, setStatusOptions] = useState([]);

  const confirmDelete = (id) => {
    confirmDialog({
      message: "Are you sure you want to delete this restaurant?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(id),
    });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/api/resturant/${id}`
      );
      setRestaurants((prev) => prev.filter((r) => r._id !== id));
      toast.success("✅ Deleted successfully!");
    } catch {
      toast.error("❌ Delete failed.");
    }
  };

  const logoTemplate = (rowData) => {
    if (!rowData.logoUrl) {
      return <span style={{ color: "#888" }}>No Logo</span>;
    }

    const logoPath = `${process.env.REACT_APP_API_BASE}${rowData.logoUrl}`;
    return (
      <img
        src={logoPath}
        alt={rowData.name}
        style={{
          width: "40px",
          height: "40px",
          objectFit: "cover",
          borderRadius: "5px",
        }}
        onError={(e) =>
          (e.target.outerHTML = `<span style="color:#888">No Logo</span>`)
        }
      />
    );
  };

  const statusTemplate = (rowData) => {
    const isActive = rowData.status === "Active";
    return (
      <Tag
        value={isActive ? "Active" : "In Active"}
        severity={isActive ? "success" : "danger"}
      />
    );
  };

  const statusFilterTemplate = (options) => {
    const dropdownOptions = statusOptions.map((status) => ({
      label: status,
      value: status,
    }));

    return (
      <Dropdown
        value={options.value}
        options={dropdownOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const actionTemplate = (rowData) => (
    <div className="action-buttons">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm"
        onClick={() => navigate(`/resturant/edit/${rowData.restaurantId}`)}
        tooltip="Edit"
      />
      <Button
        icon="pi pi-trash"
        severity="danger"
        className="p-button-sm"
        onClick={() => confirmDelete(rowData._id)}
        tooltip="Delete"
      />
    </div>
  );

  const tableHeader = (
    <div className="flex justify-content-end">
      <InputText
        value={globalFilter}
        onChange={(e) => {
          setGlobalFilter(e.target.value);
          setFilters((prev) => ({
            ...prev,
            global: {
              value: e.target.value,
              matchMode: FilterMatchMode.CONTAINS,
            },
          }));
        }}
        placeholder="Search restaurants..."
      />
    </div>
  );

  return (
    <div className="p-4">
      <h2 className="mb-3">Restaurant List</h2>
      <ConfirmDialog />

      <DataTable
        value={restaurants}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 20]}
        filters={filters}
        globalFilterFields={[
          "restaurantId",
          "name",
          "address",
          "city",
          "contactPerson",
          "phone",
          "email",
          "status",
        ]}
        filterDisplay="row"
        responsiveLayout="scroll"
        emptyMessage="No restaurants found."
        header={tableHeader}
        className="p-datatable-sm"
      >
        <Column
          field="restaurantId"
          header="ID"
          filter
          filterPlaceholder="Search by ID"
          showFilterMenu={false}
          style={{ minWidth: "180px" }}
          body={(rowData) => <div data-label="ID">{rowData.restaurantId}</div>}
        />
        <Column
          header="Logo"
          style={{ width: "100px" }}
          body={(rowData) => (
            <div data-label="Logo">{logoTemplate(rowData)}</div>
          )}
        />
        <Column
          field="name"
          header="Name"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search by Name"
          body={(rowData) => <div data-label="Name">{rowData.name}</div>}
        />
        <Column
          field="address"
          header="Address"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search by Address"
          body={(rowData) => <div data-label="Address">{rowData.address}</div>}
        />
        <Column
          field="city"
          header="City"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search by City"
          body={(rowData) => <div data-label="City">{rowData.city}</div>}
        />
        <Column
          field="contactPerson"
          header="Contact Person"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search Contact Person"
          body={(rowData) => (
            <div data-label="Contact Person">{rowData.contactPerson}</div>
          )}
        />
        <Column
          field="phone"
          header="Phone"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search by Phone"
          body={(rowData) => <div data-label="Phone">{rowData.phone}</div>}
        />
        <Column
          field="email"
          header="Email"
          filter
          showFilterMenu={false}
          filterPlaceholder="Search by Email"
          body={(rowData) => <div data-label="Email">{rowData.email}</div>}
        />
        <Column
          field="status"
          header="Status"
          filter
          filterElement={statusFilterTemplate}
          showFilterMenu={false}
          body={(rowData) => (
            <div data-label="Status">{statusTemplate(rowData)}</div>
          )}
        />
        <Column
          header="Action"
          style={{ width: "150px" }}
          body={(rowData) => (
            <div data-label="Action">{actionTemplate(rowData)}</div>
          )}
        />
      </DataTable>
    </div>
  );
}